import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { LineChart, Wallet, TrendingUp, TrendingDown, RotateCcw, Loader2, Search, Coins, Trophy, Share2, Copy, Download, Users, UserPlus, Mail, Link2, Crown, LogOut, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";

const usd = (n) => `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pl = (n) => (n >= 0 ? "text-emerald-600" : "text-red-600");

export default function PracticePage() {
  const { user, openAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  // trade form
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState("");
  const [placing, setPlacing] = useState(false);
  const [view, setView] = useState("account");
  const [orders, setOrders] = useState([]);
  const [orderType, setOrderType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const joinHandledRef = useRef(null);

  const loadAll = useCallback(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([api.get("/practice/account"), api.get("/practice/trades"), api.get("/practice/orders")])
      .then(([a, t, o]) => {
        setAccount(a.data);
        setTrades(t.data.trades);
        setOrders(o.data.orders);
      })
      .catch(() => toast.error("Could not load your practice account"))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // prefill from ?symbol= (e.g. "Trade in practice" shortcut)
  useEffect(() => {
    if (!user) return;
    const sym = searchParams.get("symbol");
    if (!sym) return;
    setView("account");
    api.get(`/practice/search?q=${encodeURIComponent(sym)}`)
      .then((r) => {
        const list = r.data.results || [];
        const match = list.find((x) => x.symbol.toUpperCase() === sym.toUpperCase()) || list[0];
        if (match) { setSelected(match); setQuery(match.symbol); setSide("buy"); setLimitPrice(String(match.price)); }
      })
      .catch(() => {})
      .finally(() => {
        searchParams.delete("symbol");
        setSearchParams(searchParams, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  // auto-join from an invite link ?join=CODE
  useEffect(() => {
    if (!user) return;
    const code = searchParams.get("join");
    if (!code) return;
    if (joinHandledRef.current === code) return;
    joinHandledRef.current = code;
    api.post("/practice/groups/join", { code })
      .then((r) => {
        toast.success(r.data.already_member ? `You're already in ${r.data.group.name}` : `Joined ${r.data.group.name}! 🎉`);
        setView("groups");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not join group"))
      .finally(() => {
        searchParams.delete("join");
        setSearchParams(searchParams, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  // open a specific tab from ?tab= (used by homepage CTAs); applies once signed in
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["account", "leaderboard", "groups"].includes(tab)) {
      setView(tab);
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // debounced search
  useEffect(() => {
    if (!user) return;
    const q = query.trim();
    if (!q || (selected && q === selected.symbol)) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(() => {
      api.get(`/practice/search?q=${encodeURIComponent(q)}`)
        .then((r) => setResults(r.data.results))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(id);
  }, [query, user, selected]);

  const pick = (r) => {
    setSelected(r);
    setResults([]);
    setQuery(r.symbol);
    setLimitPrice(String(r.price));
  };

  const placeOrder = async () => {
    const n = parseFloat(qty);
    if (!selected) { toast.error("Pick a stock or crypto to trade"); return; }
    if (!n || n <= 0) { toast.error("Enter a quantity greater than 0"); return; }
    setPlacing(true);
    try {
      if (orderType === "limit") {
        const lp = parseFloat(limitPrice);
        if (!lp || lp <= 0) { toast.error("Enter a limit price"); setPlacing(false); return; }
        const { data } = await api.post("/practice/order", { symbol: selected.symbol, side, quantity: n, limit_price: lp });
        setAccount(data.account);
        if (data.status === "filled") toast.success(`Limit order filled: ${side} ${n} ${selected.symbol}`);
        else if (data.status === "cancelled") toast.error("Order couldn't fill (funds or units).");
        else toast.success(`Limit order placed: ${side} ${n} ${selected.symbol} @ ${usd(lp)}`);
        setQty("");
      } else {
        const { data } = await api.post("/practice/trade", { symbol: selected.symbol, side, quantity: n });
        setAccount(data.account);
        toast.success(`${side === "buy" ? "Bought" : "Sold"} ${n} ${data.trade.symbol} · ${usd(data.trade.amount_usd)}`);
        setQty("");
      }
      const [t, o] = await Promise.all([api.get("/practice/trades"), api.get("/practice/orders")]);
      setTrades(t.data.trades);
      setOrders(o.data.orders);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally {
      setPlacing(false);
    }
  };

  const cancelOrder = async (id) => {
    try {
      await api.post(`/practice/order/${id}/cancel`);
      toast.success("Order cancelled");
      const [o, a] = await Promise.all([api.get("/practice/orders"), api.get("/practice/account")]);
      setOrders(o.data.orders);
      setAccount(a.data);
    } catch {
      toast.error("Could not cancel order");
    }
  };

  const doReset = async () => {
    try {
      const { data } = await api.post("/practice/reset");
      setAccount(data.account);
      setTrades([]);
      setSelected(null); setQuery(""); setQty("");
      toast.success("Practice account reset to $150,000");
    } catch {
      toast.error("Could not reset account");
    }
  };

  const sellPosition = (p) => {
    setSelected({ symbol: p.symbol, name: p.name, price: p.price, currency: p.currency });
    setResults([]);
    setQuery(p.symbol);
    setLimitPrice(String(p.price));
    setSide("sell");
    setQty(String(p.qty));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!user) {
    if (view === "leaderboard") {
      return (
        <div className="space-y-6" data-testid="public-leaderboard-page">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
                <Trophy className="w-7 h-7 text-yellow-500" /> Leaderboard
              </h1>
              <p className="text-muted-foreground text-sm mt-1">Live standings from the Practice Trading challenge — see who's number one.</p>
            </div>
            <Button onClick={() => openAuth("register")} data-testid="public-lb-join-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
              🚀 Create free account to join
            </Button>
          </div>
          <Leaderboard onGoToGroups={() => openAuth("register")} />
          <section className="rounded-2xl border border-primary/25 bg-primary/5 p-6 text-center">
            <h2 className="font-heading font-bold text-xl">Think you can reach number one?</h2>
            <p className="text-muted-foreground text-sm mt-1.5 max-w-xl mx-auto">
              Create your free account, get a virtual <span className="font-semibold text-foreground">$150,000</span>, and climb the board. Represent your school, club, or family.
            </p>
            <Button onClick={() => openAuth("register")} data-testid="public-lb-cta-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4 gap-1.5">
              🚀 Start Practice Trading
            </Button>
          </section>
        </div>
      );
    }
    return (
      <div data-testid="practice-signin-gate" className="max-w-lg mx-auto text-center py-20 space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-red-600/15 grid place-items-center mx-auto">
          <LineChart className="w-7 h-7 text-red-600" />
        </div>
        <h1 className="font-heading font-extrabold text-3xl">Practice Trading</h1>
        <p className="text-muted-foreground">
          Create your free account to get a virtual <span className="font-semibold text-foreground">$150,000</span> and practice trading US &amp; Canadian stocks and crypto at live prices — risk free. Then climb the leaderboard and represent your team.
        </p>
        <Button onClick={() => openAuth("register")} data-testid="practice-signin-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          🚀 Create your free account
        </Button>
      </div>
    );
  }

  const est = selected && qty ? (orderType === "limit" ? parseFloat(limitPrice || 0) : selected.price) * parseFloat(qty || 0) : 0;
  const openOrders = orders.filter((o) => o.status === "open");

  return (
    <div data-testid="practice-page" className="space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
            <LineChart className="w-7 h-7 text-red-600" /> Practice Trading
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Virtual paper-trading account · live prices · no real money</p>
          <span data-testid="virtual-money-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-3 py-1 text-xs font-semibold">
            <Coins className="w-3.5 h-3.5" /> Virtual dollars only — no real money is used or at risk
          </span>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="practice-reset-btn" className="gap-2">
              <RotateCcw className="w-4 h-4" /> Reset account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="practice-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset your practice account?</AlertDialogTitle>
              <AlertDialogDescription>
                This starts you over from scratch: your cash returns to $150,000, and all positions and trade history are permanently deleted. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="practice-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doReset} data-testid="practice-reset-confirm" className="bg-red-600 hover:bg-red-700">
                Yes, start over
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="inline-flex rounded-lg bg-secondary p-1" data-testid="practice-view-toggle">
        {[["account", "My Account"], ["leaderboard", "Leaderboard"], ["groups", "Groups"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`practice-view-${k}`}
            onClick={() => setView(k)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {view === "leaderboard" ? (
        <Leaderboard onGoToGroups={() => setView("groups")} />
      ) : view === "groups" ? (
        <Groups />
      ) : loading || !account ? (
        <div className="h-64 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-red-600" /></div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="practice-summary">
            <SummaryCard label="Portfolio Value" value={usd(account.equity_usd)} sub={`Virtual · ≈ C$${Number(account.equity_cad ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`} icon={Wallet} testid="summary-equity" />
            <SummaryCard label="Buying Power" value={usd(account.cash_usd)} sub="Virtual cash (USD) — not real" icon={Coins} testid="summary-cash" />
            <SummaryCard
              label="Total P/L"
              value={`${account.total_pl_usd >= 0 ? "+" : ""}${usd(account.total_pl_usd)}`}
              sub={`${account.total_pl_pct >= 0 ? "+" : ""}${account.total_pl_pct}% vs $150,000`}
              valueClass={pl(account.total_pl_usd)}
              icon={account.total_pl_usd >= 0 ? TrendingUp : TrendingDown}
              testid="summary-total-pl"
            />
            <SummaryCard
              label="Today's P/L"
              value={`${account.day_pl_usd >= 0 ? "+" : ""}${usd(account.day_pl_usd)}`}
              sub="Change today (USD)"
              valueClass={pl(account.day_pl_usd)}
              icon={account.day_pl_usd >= 0 ? TrendingUp : TrendingDown}
              testid="summary-day-pl"
            />
          </div>

          <PracticeEquityChart refreshKey={trades.length} />

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Trade panel */}
            <div className="lg:col-span-1 rounded-2xl border border-border bg-card p-5 space-y-4 h-fit" data-testid="trade-panel">
              <h3 className="font-heading font-bold text-lg">Place an order</h3>

              <div className="relative">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="trade-search-input"
                    placeholder="Search AAPL, TD.TO, BTC-USD…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                    className="pl-9"
                  />
                </div>
                {(searching || results.length > 0) && (
                  <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-72 overflow-y-auto" data-testid="trade-search-results">
                    {searching && <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>}
                    {results.map((r) => (
                      <button
                        key={r.symbol}
                        data-testid={`trade-result-${r.symbol}`}
                        onClick={() => pick(r)}
                        className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center justify-between gap-2"
                      >
                        <span className="min-w-0">
                          <span className="font-num font-semibold">{r.symbol}</span>
                          <span className="text-xs text-muted-foreground ml-2 truncate">{r.name}</span>
                        </span>
                        <span className="font-num text-sm shrink-0">{usd(r.price)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selected && (
                <div className="rounded-lg bg-secondary/50 p-3 text-sm" data-testid="trade-selected">
                  <div className="flex items-center justify-between">
                    <span className="font-num font-semibold">{selected.symbol}</span>
                    <span className="font-num">{usd(selected.price)} <span className="text-xs text-muted-foreground">{selected.currency}</span></span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{selected.name}</div>
                </div>
              )}

              <div className="inline-flex w-full rounded-lg bg-secondary p-1" data-testid="order-type-toggle">
                {[["market", "Market"], ["limit", "Limit"]].map(([k, l]) => (
                  <button
                    key={k}
                    data-testid={`order-type-${k}`}
                    onClick={() => setOrderType(k)}
                    className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                      orderType === k ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div className="inline-flex w-full rounded-lg bg-secondary p-1" data-testid="trade-side-toggle">
                {[["buy", "Buy"], ["sell", "Sell"]].map(([k, l]) => (
                  <button
                    key={k}
                    data-testid={`trade-side-${k}`}
                    onClick={() => setSide(k)}
                    className={`flex-1 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                      side === k ? (k === "buy" ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Quantity</label>
                <Input
                  data-testid="trade-qty-input"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="0"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </div>

              {orderType === "limit" && (
                <div data-testid="trade-limit-wrap">
                  <label className="text-xs text-muted-foreground">Limit price ({selected?.currency || "USD"})</label>
                  <Input
                    data-testid="trade-limit-input"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0.00"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {side === "buy" ? "Fills when the price drops to or below this." : "Fills when the price rises to or above this."}
                  </p>
                </div>
              )}

              {selected && qty > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="trade-estimate">
                  <span className="text-muted-foreground">Estimated {side === "buy" ? "cost" : "proceeds"}</span>
                  <span className="font-num font-semibold">{usd(est)} <span className="text-xs text-muted-foreground">{selected.currency}</span></span>
                </div>
              )}

              <Button
                onClick={placeOrder}
                disabled={placing || !selected}
                data-testid="trade-submit-btn"
                className={`w-full text-white ${side === "buy" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {placing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {orderType === "limit" ? "Place limit " : ""}{side === "buy" ? "Buy" : "Sell"} {selected ? selected.symbol : ""}
              </Button>

              {openOrders.length > 0 && (
                <div className="pt-2 border-t border-border space-y-2" data-testid="open-orders">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Open limit orders</div>
                  {openOrders.map((o) => (
                    <div key={o.id} data-testid={`open-order-${o.id}`} className="flex items-center justify-between text-sm rounded-lg bg-secondary/40 px-3 py-2">
                      <div>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${o.side === "buy" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>{o.side}</span>
                        <span className="font-num font-semibold ml-1.5">{o.symbol}</span>
                        <span className="text-muted-foreground"> ×{o.quantity} @ {usd(o.limit_price)}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" data-testid={`order-cancel-${o.id}`} onClick={() => cancelOrder(o.id)}>Cancel</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Positions + history */}
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-border bg-card p-5" data-testid="positions-card">
                <h3 className="font-heading font-bold text-lg mb-3">Your positions</h3>
                {account.positions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="positions-empty">
                    No positions yet. Search for a stock or crypto and place your first practice trade.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                          <th className="py-2 pr-3">Symbol</th>
                          <th className="py-2 px-2 text-right">Qty</th>
                          <th className="py-2 px-2 text-right">Avg cost</th>
                          <th className="py-2 px-2 text-right">Price</th>
                          <th className="py-2 px-2 text-right">Value (USD)</th>
                          <th className="py-2 px-2 text-right">P/L</th>
                          <th className="py-2 pl-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.positions.map((p) => (
                          <tr key={p.symbol} data-testid={`position-${p.symbol}`} className="border-b border-border/60 last:border-0">
                            <td className="py-2.5 pr-3">
                              <div className="font-num font-semibold">{p.symbol}</div>
                              <div className="text-[11px] text-muted-foreground truncate max-w-[140px]">{p.name}</div>
                            </td>
                            <td className="py-2.5 px-2 text-right font-num">{p.qty}</td>
                            <td className="py-2.5 px-2 text-right font-num">{usd(p.avg_cost_usd)}</td>
                            <td className="py-2.5 px-2 text-right font-num">{usd(p.price)} <span className="text-[10px] text-muted-foreground">{p.currency}</span></td>
                            <td className="py-2.5 px-2 text-right font-num">{usd(p.market_value_usd)}</td>
                            <td className={`py-2.5 px-2 text-right font-num ${pl(p.pl_usd)}`}>
                              {p.pl_usd >= 0 ? "+" : ""}{usd(p.pl_usd)}<div className="text-[11px]">{p.pl_pct >= 0 ? "+" : ""}{p.pl_pct}%</div>
                            </td>
                            <td className="py-2.5 pl-2 text-right">
                              <Button size="sm" variant="outline" data-testid={`position-sell-${p.symbol}`} onClick={() => sellPosition(p)}>Sell</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-border bg-card p-5" data-testid="trades-card">
                <h3 className="font-heading font-bold text-lg mb-3">Recent trades</h3>
                {trades.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center" data-testid="trades-empty">No trades yet.</p>
                ) : (
                  <div className="space-y-2">
                    {trades.map((t, i) => (
                      <div key={`${t.ts}-${t.symbol}-${t.side}-${i}`} data-testid={`trade-row-${i}`} className="flex items-center justify-between text-sm rounded-lg bg-secondary/40 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${t.side === "buy" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>{t.side}</span>
                          <span className="font-num font-semibold">{t.symbol}</span>
                          <span className="text-muted-foreground">× {t.quantity}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-num">{usd(t.amount_usd)}</div>
                          <div className="text-[11px] text-muted-foreground">@ {usd(t.price_native)} {t.currency}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, valueClass = "", icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${valueClass || "text-muted-foreground"}`} />}
      </div>
      <div className={`font-num font-extrabold text-2xl mt-2 ${valueClass}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

const PRANGES = ["1W", "1M", "3M", "1Y"];

function PracticeEquityChart({ refreshKey }) {
  const [range, setRange] = useState("1M");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/practice/history", { params: { range } })
      .then((r) => setData(r.data))
      .catch(() => setData({ points: [], invested: 150000 }))
      .finally(() => setLoading(false));
  }, [range, refreshKey]);

  const pts = data?.points || [];
  const first = pts[0]?.value, last = pts[pts.length - 1]?.value;
  const change = first && last ? last - first : 0;
  const up = change >= 0;
  const hex = up ? "#059669" : "#dc2626";

  return (
    <div data-testid="practice-equity-chart" className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-bold">Account value <span className="text-xs font-num text-muted-foreground">(USD)</span></div>
          {pts.length > 1 && (
            <div className={`font-num text-sm ${up ? "text-emerald-600" : "text-red-600"}`} data-testid="practice-chart-change">
              {up ? "+" : "-"}{usd(Math.abs(change))} over {range}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {PRANGES.map((r) => (
            <button key={r} data-testid={`practice-range-${r}`} onClick={() => setRange(r)} className={`px-3 py-1 rounded-md text-xs font-num font-semibold transition-colors ${range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{r}</button>
          ))}
        </div>
      </div>
      <div className="h-56 sm:h-64 p-2">
        {loading && !pts.length ? (
          <div className="h-full grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : pts.length < 2 ? (
          <div className="h-full grid place-items-center text-sm text-muted-foreground" data-testid="practice-chart-empty">Your account value chart will appear as you trade over time.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={pts} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="pev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={hex} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={hex} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis domain={["auto", "auto"]} hide />
              {data?.invested && <ReferenceLine y={data.invested} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: "Start $150k", position: "insideTopLeft", fontSize: 10, fill: "#64748b" }} />}
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid #BFD7F5", color: "#0B1F4D", borderRadius: 8, fontSize: 12 }}
                labelFormatter={(l) => l}
                formatter={(v) => [usd(v), "Value"]}
              />
              <Area type="monotone" dataKey="value" stroke={hex} strokeWidth={2} fill="url(#pev)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
function Leaderboard({ onGoToGroups }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spot, setSpot] = useState(null);

  useEffect(() => {
    api.get("/practice/leaderboard")
      .then((r) => setData(r.data))
      .catch(() => setData({ leaderboard: [], me: null, total_traders: 0 }))
      .finally(() => setLoading(false));
    api.get("/practice/groups/spotlight").then((r) => setSpot(r.data)).catch(() => setSpot(null));
  }, []);

  if (loading) return <div className="h-64 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-red-600" /></div>;

  const rows = data.leaderboard || [];
  const medal = (rank) => (rank === 1 ? "text-yellow-500" : rank === 2 ? "text-slate-400" : rank === 3 ? "text-amber-700" : "text-muted-foreground");
  const champ = data.champion;
  const fmtMonth = (m) => { if (!m) return ""; const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" }); };
  const fmtReset = (d) => (d ? new Date(d + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

  return (
    <div className="space-y-4" data-testid="leaderboard-view">
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent p-4 flex items-start gap-3" data-testid="contest-banner">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 grid place-items-center shrink-0"><Trophy className="w-5 h-5 text-amber-500" /></div>
        <div className="min-w-0">
          <div className="font-heading font-semibold">Monthly Trading Contest</div>
          {champ ? (
            <p className="text-sm text-muted-foreground" data-testid="contest-champion">
              🏆 <span className="font-semibold text-foreground">{champ.name}</span> won {fmtMonth(champ.month)}{champ.return_pct != null ? ` with ${champ.return_pct >= 0 ? "+" : ""}${champ.return_pct}%` : ""}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Be the first Top Trader — trade this month to compete for the badge.</p>
          )}
          <p className="text-xs text-muted-foreground mt-0.5">
            All accounts reset to {usd(150000)} on <span className="font-semibold text-foreground">{fmtReset(data.next_reset)}</span>. The month's #1 earns the <span className="text-amber-600 font-semibold">Top Trader</span> badge.
          </p>
        </div>
      </div>

      {spot && (
        <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-4 flex items-center gap-3 flex-wrap" data-testid="group-spotlight">
          <div className="w-10 h-10 rounded-xl bg-primary/15 grid place-items-center shrink-0"><Users className="w-5 h-5 text-primary" /></div>
          {spot.none ? (
            <>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold">Trade with friends</div>
                <p className="text-sm text-muted-foreground">Start a private league, invite your crew, and battle on your own leaderboard.</p>
              </div>
              <Button size="sm" onClick={onGoToGroups} data-testid="spotlight-start-btn" className="gap-1.5 shrink-0"><Users className="w-4 h-4" /> Start a league</Button>
            </>
          ) : (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-primary font-semibold">🔥 Group of the week</div>
                <div className="font-heading font-semibold truncate" data-testid="spotlight-name">{spot.name}</div>
                <p className="text-sm text-muted-foreground font-num">
                  {spot.member_count} member{spot.member_count === 1 ? "" : "s"} · leader
                  <span className={spot.top_return_pct >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}> {spot.top_return_pct >= 0 ? "+" : ""}{spot.top_return_pct}%</span>
                </p>
              </div>
              <Button size="sm" onClick={onGoToGroups} data-testid="spotlight-start-btn" className="gap-1.5 shrink-0"><Users className="w-4 h-4" /> Start your own</Button>
            </>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" /> Top practice traders
          </h3>
          <span className="text-xs text-muted-foreground" data-testid="leaderboard-total">{data.total_traders} trader{data.total_traders === 1 ? "" : "s"} competing</span>
        </div>

        {data.me && (
          <div className="mt-3 rounded-lg bg-primary/10 border border-primary/20 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-sm" data-testid="leaderboard-me">
            <span className="font-semibold">Your rank: <span className="font-num">#{data.me.rank}</span> of {data.total_traders}</span>
            <div className="flex items-center gap-3">
              {data.me.badges > 0 && (
                <span className="inline-flex items-center gap-0.5 text-amber-600 text-xs font-semibold" data-testid="me-badges"><Trophy className="w-3.5 h-3.5" />{data.me.badges} Top Trader</span>
              )}
              <span className={`font-num font-semibold ${data.me.return_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {data.me.return_pct >= 0 ? "+" : ""}{data.me.return_pct}%
              </span>
              <ShareRank me={data.me} total={data.total_traders} name={user?.name || "A trader"} />
            </div>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="leaderboard-empty">
            No ranked traders yet — place your first trade to get on the board!
          </p>
        ) : (
          <div className="mt-4 space-y-1.5">
            {rows.map((r) => (
              <div
                key={r.rank}
                data-testid={`leaderboard-row-${r.rank}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${r.is_me ? "bg-primary/10 border border-primary/20" : "bg-secondary/40"}`}
              >
                <span className={`font-num font-extrabold w-8 text-center ${medal(r.rank)}`}>{r.rank <= 3 ? <Trophy className={`w-4 h-4 inline ${medal(r.rank)}`} /> : `#${r.rank}`}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    {r.name}
                    {r.is_demo && (
                      <span className="inline-flex items-center rounded-full bg-slate-500/15 text-slate-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide" title="Illustrative demo account" data-testid={`row-demo-${r.rank}`}>Demo</span>
                    )}
                    {r.badges > 0 && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 text-amber-600 px-1.5 py-0.5 text-[10px] font-semibold" title={`${r.badges} Top Trader badge${r.badges === 1 ? "" : "s"}`} data-testid={`row-badges-${r.rank}`}>
                        <Trophy className="w-3 h-3" />{r.badges}
                      </span>
                    )}
                    {r.is_me && <span className="text-xs text-primary">(You)</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-num">{r.trades} trade{r.trades === 1 ? "" : "s"} · {usd(r.equity_usd)}</div>
                </div>
                <span className={`font-num font-bold ${r.return_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {r.return_pct >= 0 ? "+" : ""}{r.return_pct}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ShareRank({ me, total, name }) {
  const shareUrl = `${window.location.origin}/practice`;
  const pos = me.return_pct >= 0;
  const message = `${name} is ranked #${me.rank} of ${total} on STEWART CAP Practice Trading with a ${pos ? "+" : ""}${me.return_pct}% return. Can you beat it? Trade US, TSX & crypto risk-free: ${shareUrl}`;
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const renderPng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await renderPng();
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = `stewart-cap-rank-${me.rank}.png`;
      a.click();
      toast.success("Image saved");
    } catch {
      toast.error("Could not create image");
    } finally {
      setBusy(false);
    }
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      // Try to share the image file itself where supported
      try {
        const url = await renderPng();
        if (url) {
          const blob = await (await fetch(url)).blob();
          const file = new File([blob], `stewart-cap-rank-${me.rank}.png`, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "STEWART CAP Practice Trading", text: message });
            return;
          }
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
      if (navigator.share) {
        try {
          await navigator.share({ title: "STEWART CAP Practice Trading", text: message, url: shareUrl });
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return;
        }
      }
      await navigator.clipboard.writeText(message);
      toast.success("Rank copied — paste it anywhere to share");
    } catch {
      toast.error("Could not share on this device");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" className="gap-1.5" data-testid="share-rank-btn">
          <Share2 className="w-3.5 h-3.5" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-rank-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Share your rank</DialogTitle>
        </DialogHeader>

        <div
          ref={cardRef}
          data-testid="share-rank-card"
          className="rounded-2xl p-6 text-white text-center space-y-3 shadow-xl"
          style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#b91c1c 100%)" }}
        >
          <div className="text-[11px] font-num uppercase tracking-widest text-white/80 flex items-center justify-center gap-1.5">
            <LineChart className="w-3.5 h-3.5" /> STEWART CAP · Practice Trading
          </div>
          <Trophy className="w-9 h-9 mx-auto text-yellow-300" />
          <div className="font-heading font-extrabold text-4xl">Rank #{me.rank}<span className="text-lg font-num font-medium text-white/70"> / {total}</span></div>
          <div className={`font-num font-extrabold text-3xl ${pos ? "text-emerald-300" : "text-red-300"}`}>
            {pos ? "+" : ""}{me.return_pct}%
          </div>
          <div className="text-sm text-white/80 font-num">Portfolio {usd(me.equity_usd)} · {me.trades} trade{me.trades === 1 ? "" : "s"}</div>
          <div className="text-xs text-white/70 pt-1">Trade US, TSX &amp; crypto — risk free</div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Button onClick={nativeShare} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="share-rank-native">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
          </Button>
          <Button onClick={download} disabled={busy} variant="outline" className="gap-1.5" data-testid="share-rank-download">
            <Download className="w-4 h-4" /> Save
          </Button>
          <Button onClick={copyLink} variant="outline" className="gap-1.5" data-testid="share-rank-copy">
            <Copy className="w-4 h-4" /> Copy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Groups() {
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const load = () => api.get("/practice/groups").then((r) => setGroups(r.data.groups)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const n = name.trim();
    if (n.length < 2) { toast.error("Give your group a name"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/practice/groups", { name: n });
      toast.success(`Group "${data.group.name}" created`);
      setName("");
      await load();
      setOpenGroup(data.group.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create group");
    } finally { setCreating(false); }
  };

  const join = async () => {
    const c = joinCode.trim();
    if (!c) { toast.error("Enter a group code"); return; }
    setJoining(true);
    try {
      const { data } = await api.post("/practice/groups/join", { code: c });
      toast.success(data.already_member ? `Already in ${data.group.name}` : `Joined ${data.group.name}! 🎉`);
      setJoinCode("");
      await load();
      setOpenGroup(data.group.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join group");
    } finally { setJoining(false); }
  };

  return (
    <div className="space-y-5" data-testid="groups-view">
      <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 grid place-items-center shrink-0"><Users className="w-5 h-5 text-primary" /></div>
        <div className="min-w-0">
          <div className="font-heading font-semibold">Trading leagues</div>
          <p className="text-sm text-muted-foreground">Create a private group, invite friends by email or share a link, and battle it out on your own group leaderboard — everyone trades the same virtual $150,000.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="group-create-card">
          <h3 className="font-heading font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-primary" /> Create a group</h3>
          <Input data-testid="group-name-input" placeholder="e.g. Office League, Family Cup…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} maxLength={50} />
          <Button onClick={create} disabled={creating} data-testid="group-create-btn" className="w-full gap-1.5">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create group
          </Button>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="group-join-card">
          <h3 className="font-heading font-bold flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" /> Join with a code</h3>
          <Input data-testid="group-join-input" placeholder="Enter group code (e.g. A1B2C3)" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && join()} maxLength={16} className="font-num" />
          <Button onClick={join} disabled={joining} variant="outline" data-testid="group-join-btn" className="w-full gap-1.5">
            {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Join group
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5" data-testid="group-list-card">
        <h3 className="font-heading font-bold text-lg mb-3">Your groups</h3>
        {groups === null ? (
          <div className="h-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="groups-empty">You're not in any groups yet. Create one above or join with a friend's code.</p>
        ) : (
          <div className="space-y-2">
            {groups.map((g) => (
              <button key={g.id} data-testid={`group-row-${g.id}`} onClick={() => setOpenGroup(g.id)} className="w-full flex items-center justify-between gap-3 rounded-lg bg-secondary/40 hover:bg-secondary px-4 py-3 text-left transition-colors">
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-1.5">
                    {g.name}
                    {g.is_owner && <Crown className="w-3.5 h-3.5 text-amber-500" title="You own this group" />}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-num">{g.member_count} member{g.member_count === 1 ? "" : "s"} · code {g.code}</div>
                </div>
                <span className="text-xs text-primary font-semibold shrink-0">View →</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <GroupDetailDialog groupId={openGroup} onClose={() => setOpenGroup(null)} onChanged={load} />
    </div>
  );
}

function GroupDetailDialog({ groupId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState("");

  const load = useCallback(() => {
    if (!groupId) return;
    setLoading(true);
    api.get(`/practice/groups/${groupId}`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Could not load group"); onClose(); })
      .finally(() => setLoading(false));
  }, [groupId, onClose]);

  useEffect(() => { if (groupId) { setData(null); setInviteLink(""); setEmail(""); load(); } }, [groupId, load]);

  const g = data?.group;
  const joinLink = g ? `${window.location.origin}/practice?join=${g.code}` : "";

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy"); }
  };

  const sendInvite = async () => {
    const e = email.trim();
    if (!e) { toast.error("Enter an email to invite"); return; }
    setInviting(true);
    try {
      const { data: res } = await api.post(`/practice/groups/${groupId}/invite`, { email: e });
      toast[res.sent ? "success" : "message"](res.message);
      if (!res.sent && res.join_url) setInviteLink(res.join_url);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send invite");
    } finally { setInviting(false); }
  };

  const leave = async () => {
    try { await api.post(`/practice/groups/${groupId}/leave`); toast.success("You left the group"); onChanged(); onClose(); }
    catch { toast.error("Could not leave"); }
  };
  const remove = async () => {
    try { await api.delete(`/practice/groups/${groupId}`); toast.success("Group deleted"); onChanged(); onClose(); }
    catch { toast.error("Could not delete"); }
  };

  const medal = (rank) => (rank === 1 ? "text-yellow-500" : rank === 2 ? "text-slate-400" : rank === 3 ? "text-amber-700" : "text-muted-foreground");

  return (
    <Dialog open={!!groupId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="group-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> {g?.name || "Group"}
          </DialogTitle>
          <DialogDescription>Invite friends, share your code, and track your private group leaderboard.</DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="h-40 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-red-600" /></div>
        ) : g ? (
          <div className="space-y-4">
            {/* invite code + link */}
            <div className="rounded-xl bg-secondary/50 p-3 space-y-2.5" data-testid="group-share">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Group code</div>
                  <div className="font-num font-extrabold text-xl tracking-widest" data-testid="group-code">{g.code}</div>
                </div>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="copy-code-btn" onClick={() => copy(g.code, "Code")}><Copy className="w-3.5 h-3.5" /> Code</Button>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="copy-link-btn" onClick={() => copy(joinLink, "Invite link")}><Link2 className="w-3.5 h-3.5" /> Link</Button>
              </div>
              <ShareGroup group={g} leaderboard={data.leaderboard || []} joinLink={joinLink} />
            </div>

            {/* invite by email */}
            <div className="space-y-2" data-testid="group-invite">
              <div className="text-sm font-semibold flex items-center gap-1.5"><Mail className="w-4 h-4 text-primary" /> Invite by email</div>
              <div className="flex gap-2">
                <Input data-testid="invite-email-input" type="email" placeholder="friend@email.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendInvite()} />
                <Button onClick={sendInvite} disabled={inviting} data-testid="invite-send-btn" className="gap-1.5 shrink-0">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Invite
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">We email people who already have a STEWART CAP account. For anyone else, share the link above.</p>
              {inviteLink && (
                <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs" data-testid="invite-link-fallback">
                  <span className="truncate font-num flex-1">{inviteLink}</span>
                  <button className="text-primary font-semibold shrink-0" onClick={() => copy(inviteLink, "Invite link")}>Copy</button>
                </div>
              )}
            </div>

            {/* group leaderboard */}
            <div>
              <div className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Trophy className="w-4 h-4 text-yellow-500" /> Group leaderboard</div>
              <div className="space-y-1.5" data-testid="group-leaderboard">
                {(data.leaderboard || []).map((r) => (
                  <div key={r.rank} data-testid={`group-lb-row-${r.rank}`} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${r.is_me ? "bg-primary/10 border border-primary/20" : "bg-secondary/40"}`}>
                    <span className={`font-num font-extrabold w-7 text-center ${medal(r.rank)}`}>{r.rank <= 3 ? <Trophy className={`w-4 h-4 inline ${medal(r.rank)}`} /> : `#${r.rank}`}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {r.name}
                        {r.badges > 0 && <span className="inline-flex items-center gap-0.5 text-amber-600 text-[10px] font-semibold"><Trophy className="w-3 h-3" />{r.badges}</span>}
                        {r.is_me && <span className="text-xs text-primary">(You)</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-num">{r.trades} trade{r.trades === 1 ? "" : "s"} · {usd(r.equity_usd)}</div>
                    </div>
                    <span className={`font-num font-bold ${r.return_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.return_pct >= 0 ? "+" : ""}{r.return_pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* actions */}
            <div className="flex justify-end gap-2 pt-1 border-t border-border">
              {g.is_owner ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" className="text-red-600 hover:text-red-700 gap-1.5" data-testid="group-delete-btn"><Trash2 className="w-4 h-4" /> Delete group</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="group-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this group?</AlertDialogTitle>
                      <AlertDialogDescription>This removes the group and its leaderboard for everyone. Members' own practice accounts are not affected. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="group-delete-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={remove} data-testid="group-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <Button variant="ghost" className="text-red-600 hover:text-red-700 gap-1.5" data-testid="group-leave-btn" onClick={leave}><LogOut className="w-4 h-4" /> Leave group</Button>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ShareGroup({ group, leaderboard, joinLink }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const top = (leaderboard || []).slice(0, 5);
  const message = `Join my "${group.name}" trading league on STEWART CAP Practice Trading! Everyone gets a virtual $150,000 to trade US, TSX & crypto — compete on our private leaderboard. Use code ${group.code} or tap: ${joinLink}`;

  const renderPng = async () => (cardRef.current ? toPng(cardRef.current, { pixelRatio: 2, cacheBust: true }) : null);

  const download = async () => {
    setBusy(true);
    try {
      const url = await renderPng();
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = `stewart-cap-league-${group.code}.png`;
      a.click();
      toast.success("Image saved");
    } catch { toast.error("Could not create image"); } finally { setBusy(false); }
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      try {
        const url = await renderPng();
        if (url) {
          const blob = await (await fetch(url)).blob();
          const file = new File([blob], `stewart-cap-league-${group.code}.png`, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `${group.name} · STEWART CAP league`, text: message });
            return;
          }
        }
      } catch (e) { if (e && e.name === "AbortError") return; }
      if (navigator.share) {
        try { await navigator.share({ title: `${group.name} · STEWART CAP league`, text: message, url: joinLink }); return; }
        catch (e) { if (e && e.name === "AbortError") return; }
      }
      await navigator.clipboard.writeText(message);
      toast.success("Invite copied — paste it anywhere to recruit friends");
    } catch { toast.error("Could not share on this device"); } finally { setBusy(false); }
  };

  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Invite copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" className="w-full gap-1.5" data-testid="share-group-btn">
          <Share2 className="w-4 h-4" /> Share group standings
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="share-group-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Share your league</DialogTitle>
          <DialogDescription>Recruit friends with a one-tap standings card and join code.</DialogDescription>
        </DialogHeader>

        <div
          ref={cardRef}
          data-testid="share-group-card"
          className="rounded-2xl p-5 text-white space-y-3 shadow-xl"
          style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#b91c1c 100%)" }}
        >
          <div className="text-[11px] font-num uppercase tracking-widest text-white/80 flex items-center gap-1.5">
            <LineChart className="w-3.5 h-3.5" /> STEWART CAP · Trading League
          </div>
          <div className="font-heading font-extrabold text-2xl leading-tight">{group.name}</div>
          <div className="space-y-1.5">
            {top.map((r) => (
              <div key={r.rank} className="flex items-center gap-2 text-sm">
                <span className="font-num font-bold w-5 text-yellow-300">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `#${r.rank}`}</span>
                <span className="flex-1 min-w-0 truncate font-semibold">{r.name}</span>
                <span className={`font-num font-bold ${r.return_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>{r.return_pct >= 0 ? "+" : ""}{r.return_pct}%</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-white/15 px-4 py-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/70">Join code</div>
            <div className="font-num font-extrabold text-2xl tracking-[0.3em]">{group.code}</div>
          </div>
          <div className="text-xs text-white/80 text-center">Virtual $150,000 · US, TSX &amp; crypto · risk free</div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Button onClick={nativeShare} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="share-group-native">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
          </Button>
          <Button onClick={download} disabled={busy} variant="outline" className="gap-1.5" data-testid="share-group-download">
            <Download className="w-4 h-4" /> Save
          </Button>
          <Button onClick={copyMsg} variant="outline" className="gap-1.5" data-testid="share-group-copy">
            <Copy className="w-4 h-4" /> Copy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

